import { IconsEnum } from '@modules/husleie-framework/src/enums/Icons.enum';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import { BankAccountEnum } from '@modules/husleie-framework/src/types/BankAccount.entity';
import { ElectricityProviderData } from '@modules/husleie-framework/src/types/ElectricityProviderData.entity';
import { PaymentMethodEnum } from '@modules/husleie-framework/src/types/Payment.entity';
import { UpsellStatusEnum, UpsellOpportunity, UpsellOpportunityEnum } from '@modules/husleie-framework/src/types/Upsell.entity';
import { UpsellElectricity } from '@modules/husleie-framework/src/types/UpsellElectricity.entity';
import type {
  ElectricityProviderDataGet,
  ElectricityProviderDataGetResponse,
  UpsellAPIInterface,
  UpsellElectricityCreate,
  UpsellElectricityCreateResponse,
  UpsellElectricityGet,
  UpsellElectricityGetResponse,
  OpportunitiesGet,
  OpportunitiesGetResponse,
  DepositSubscriptionCreate,
  DepositSubscriptionCreateResponse,
  DepositSubscriptionGetDetails,
  DepositSubscriptionGetDetailsResponse,
  DepositAccountUpdateKYC,
  DepositAccountUpdateKYCResponse,
} from '@modules/store/upsell/types/UpsellAPI.interface';
import {
  DepositSubscriptionGet,
  DepositSubscriptionGetResponse,
  CreditCheckGet,
  CreditCheckGetResponse,
  DepositAccountGetDetails,
  DepositAccountGetDetailsResponse,
  DepositAccountGetSigningURL,
  DepositAccountGetSigningURLResponse,
  DepositAccountGet,
  DepositAccountGetResponse,
  SecurityDepositGet,
  SecurityDepositGetReponse,
  SecurityDepositGetDetails,
  SecurityDepositGetDetailsResponse,
  SecurityDepositUpdateKYC,
  SecurityDepositUpdateKYCResponse,
  SecurityDepositGetSigningURL,
  SecurityDepositGetSigningURLResponse,
  TenancySubscriptionGet,
  TenancySubscriptionGetResponse,
  TenancySubscriptionGetDetails,
  TenancySubscriptionGetDetailsResponse,
  TenancySubscriptionCreate,
  TenancySubscriptionCreateResponse,
} from '@modules/store/upsell/types/UpsellAPI.interface';

export default class API implements UpsellAPIInterface {
  /* Base */

  getOpportunities(get: OpportunitiesGet): Promise<OpportunitiesGetResponse> {
    const data: UpsellOpportunity[] = [
      { type: UpsellOpportunityEnum.ELECTRICITY },
      { type: UpsellOpportunityEnum.DEPOSIT_SUBSCRIPTION },
      { type: UpsellOpportunityEnum.TENANCY_SUBSCRIPTION, status: UpsellStatusEnum.OPEN },
    ];

    LogOperations.debug('API|upsell|MOCK getOpportunities::GET body', get);
    LogOperations.debug('API|upsell|MOCK getOpportunities::GET response', data);

    return new Promise(resolve => resolve({ opportunities: data }));
  }

  /* Electricity */

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  getElectricityProviderData(get: ElectricityProviderDataGet): Promise<ElectricityProviderDataGetResponse> {
    const data: ElectricityProviderData = {
      providerName: 'Stadtwerke Erkrath GmbH',
      // cancellationPeriod: '2 Wochen',
      kwhPrice: 0.2562,
      monthlyPrice: 88.99,
      yearlyPrice: 1067.83,
      savings: -21.36,
      tariff: {
        basePrice: 14.26,
        date: '2021-10-12',
        id: '12',
        previousVolume: 3500,
        priceGuarantee: '12',
        workPrice: 0.2562,
      },
    };

    LogOperations.debug('API|upsell|MOCK getElectricityTariff::GET response', data);

    return new Promise(resolve => resolve({ electricityProviderData: data }));
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  getElectricity(get: UpsellElectricityGet): Promise<null | UpsellElectricityGetResponse> {
    const data: UpsellElectricity = {
      partner: {
        name: 'SWK',
        component: 'organisms/upsell/partners/SWK',
      },
      buttons: {
        value: UpsellStatusEnum.PRISTINE,
        options: [
          {
            name: 'UpsellAgree',
            value: UpsellStatusEnum.INTERESTED,
            icon: IconsEnum.HAPPY_FACE,
            term: 'upsell.electricity.btn.order',
          },
          {
            name: 'UpsellDecline',
            value: UpsellStatusEnum.DECLINED,
            icon: IconsEnum.THUMB_DOWN,
            term: 'upsell.electricity.btn.decline',
          },
        ],
        id: 'swk-upsell',
        config: 'orientation=landscape,mode=fancy',
      },
      status: UpsellStatusEnum.PRISTINE,
      customer: {
        payment: {
          method: PaymentMethodEnum.SEPA,
          bankAccount: {
            type: BankAccountEnum.IBAN,
            number: 'DE89370400440532013000',
            additionalNumber: 'COBADEFFXXX',
            firstName: 'Willi',
            lastName: 'Vanilli',
            address: {
              street: 'Willi Street',
              postalCode: '11111',
              postalArea: 'M City',
              country: {
                code: 'XX',
              },
            },
          },
        },
      },
    };

    LogOperations.debug('API|upsell|MOCK getElectricity::GET response', data);

    return new Promise(resolve => resolve({ upsellElectricity: data }));
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  createElectricity(create: UpsellElectricityCreate): Promise<UpsellElectricityCreateResponse> {
    const data: UpsellElectricity = {
      partner: {
        name: 'SWK',
      },
      buttons: {
        value: 'Tenant',
        options: [
          {
            name: 'UpsellAgree',
            value: UpsellStatusEnum.INTERESTED,
            icon: IconsEnum.HAPPY_FACE,
            term: 'upsell.electricity.btn.order',
          },
          {
            name: 'UpsellDecline',
            value: UpsellStatusEnum.DECLINED,
            icon: IconsEnum.THUMB_DOWN,
            term: 'upsell.electricity.btn.decline',
          },
        ],
        id: 'swk-upsell',
        config: 'mode=fancy',
      },
      status: UpsellStatusEnum.PRISTINE,
      customer: {
        payment: {
          method: PaymentMethodEnum.SEPA,
          bankAccount: {
            type: BankAccountEnum.IBAN,
            number: 'DE89370400440532013000',
            additionalNumber: 'COBADEFFXXX',
            firstName: 'Willi',
            lastName: 'Vanilli',
            address: {
              street: 'Willi Street',
              postalCode: '11111',
              postalArea: 'M City',
              country: {
                code: 'XX',
              },
            },
          },
        },
      },
    };

    LogOperations.debug('API|upsell|MOCK createElectricity::POST body', data);

    return new Promise(resolve => resolve({ upsellElectricity: data }));
  }

  getDepositSubscriptionDetails(get: DepositSubscriptionGetDetails): Promise<DepositSubscriptionGetDetailsResponse> {
    // New subscription request
    const dataNew = {
      monthlyPrice: 123.0,
      setupFee: 250.0,
      currency: 'NOK',
      token: 'deposit subscription token',
    };

    // Existing subscription request
    const dataExisting = 'deposit subscription id';

    const newOnly = true;

    LogOperations.debug('API|upsell|MOCK getDepositSubscriptionDetails::GET data', get);
    LogOperations.debug('API|upsell|MOCK getDepositSubscriptionDetails::GET response', dataNew);

    return new Promise(resolve => resolve({ upsellPrice: newOnly ? dataNew : undefined }));
  }

  createDepositSubscription(create: DepositSubscriptionCreate): Promise<DepositSubscriptionCreateResponse> {
    const data = 'deposit subscription id';

    LogOperations.debug('API|upsell|MOCK createDepositSubscription::POST data', create);
    LogOperations.debug('API|upsell|MOCK createDepositSubscription::POST body', data);

    return new Promise(resolve => resolve({ upsellId: data }));
  }

  getDepositSubscription(get: DepositSubscriptionGet): Promise<DepositSubscriptionGetResponse> {
    const data = {
      status: UpsellStatusEnum.CREDTIT_CHECK,
      token: 'deposit subscription credit check token',
    };

    LogOperations.debug('API|upsell|MOCK getDepositSubscription::GET data', get);
    LogOperations.debug('API|upsell|MOCK getDepositSubscription::GET body', data);

    return new Promise(resolve => resolve({ upsellDepositSubscription: data }));
  }

  getCreditCheck(get: CreditCheckGet): Promise<CreditCheckGetResponse> {
    const data = {
      approved: true,
    };

    LogOperations.debug('API|upsell|MOCK getCreditCheck::GET data', get);
    LogOperations.debug('API|upsell|MOCK getCreditCheck::GET body', data);

    return new Promise(resolve => resolve({ creditCheck: { approved: data.approved } }));
  }

  /* Deposit Account */

  getDepositAccountDetails(get: DepositAccountGetDetails): Promise<DepositAccountGetDetailsResponse> {
    const data = {
      kyc: {
        firstName: 'firstname',
      },
    };

    LogOperations.debug('API|upsell|MOCK getDepositAccount::GET details data', get);
    LogOperations.debug('API|upsell|MOCK getDepositAccount::GET detail response', data);

    return new Promise(resolve => resolve({ uuid: 'uuid', kyc: data.kyc }));
  }

  updateDepositAccountKYC(update: DepositAccountUpdateKYC): Promise<DepositAccountUpdateKYCResponse> {
    LogOperations.debug('API|upsell|MOCK getDepositAccount::GET KYC data', update);
    LogOperations.debug('API|upsell|MOCK getDepositAccount::GET KYC response', {});

    return new Promise(resolve => resolve({}));
  }

  getDepositAccountSigningURL(get: DepositAccountGetSigningURL): Promise<DepositAccountGetSigningURLResponse> {
    const data = 'https://signing.com/test';

    LogOperations.debug('API|upsell|MOCK getDepositAccount::GET signing URL data', get);
    LogOperations.debug('API|upsell|MOCK getDepositAccount::GET signing URL response', data);

    return new Promise(resolve => resolve({ signingURL: data }));
  }

  getDepositAccountLegacy(get: DepositAccountGet): Promise<DepositAccountGetResponse> {
    const data = {
      status: UpsellStatusEnum.CREDTIT_CHECK,
    };

    LogOperations.debug('API|upsell|MOCK getDepositAccount::GET data', get);
    LogOperations.debug('API|upsell|MOCK getDepositAccount::GET response', data);

    return new Promise(resolve => resolve({ upsellDepositAccount: data }));
  }

  getDepositAccount(get: DepositAccountGet): Promise<DepositAccountGetResponse> {
    const data = {
      status: UpsellStatusEnum.CREDTIT_CHECK,
    };

    LogOperations.debug('API|upsell|MOCK getDepositAccount::GET data', get);
    LogOperations.debug('API|upsell|MOCK getDepositAccount::GET response', data);

    return new Promise(resolve => resolve({ upsellDepositAccount: data }));
  }

  /* Security Deposit */

  getSecurityDeposit(get: SecurityDepositGet): Promise<SecurityDepositGetReponse> {
    const data = {
      status: UpsellStatusEnum.CREDTIT_CHECK,
    };

    LogOperations.debug('API|upsell|MOCK getSecurityDeposit::GET data', get);
    LogOperations.debug('API|upsell|MOCK getSecurityDeposit::GET response', data);

    return new Promise(resolve => resolve({ upsellSecurityDeposit: data }));
  }

  getSecurityDepositDetails(get: SecurityDepositGetDetails): Promise<SecurityDepositGetDetailsResponse> {
    const data = {
      uuid: 'uuid',
	  satus: 'draft',
      kyc: {
        firstName: 'firstname',
      },
    };

    LogOperations.debug('API|upsell|MOCK getSecurityDepositDetails::GET data', get);
    LogOperations.debug('API|upsell|MOCK getSecurityDepositDetails::GET response', data);

    return new Promise(resolve => resolve({ ...data }));
  }

  updateSecurityDepositKYC(update: SecurityDepositUpdateKYC): Promise<SecurityDepositUpdateKYCResponse> {
    LogOperations.debug('API|upsell|MOCK updateSecurityDepositKYC::POST data', update);
    LogOperations.debug('API|upsell|MOCK updateSecurityDepositKYC::POST response', {});

    return new Promise(resolve => resolve({}));
  }

  getSecurityDepositSigningURL(get: SecurityDepositGetSigningURL): Promise<SecurityDepositGetSigningURLResponse> {
    const data = 'https://signing.com/test';

    LogOperations.debug('API|upsell|MOCK getSecurityDepositSigningURL::GET data', get);
    LogOperations.debug('API|upsell|MOCK getSecurityDepositSigningURL::GET response', data);

    return new Promise(resolve => resolve({ signingURL: data }))
  }

  /* Tenancy Subscription */

  getTenancySubscriptionDetails(get: TenancySubscriptionGetDetails): Promise<TenancySubscriptionGetDetailsResponse> {
    const data = {
      currency: 'data.currency',
      includedVat: 'data.includedVat',
      monthlyPrice: 'data.monthlyPrice',
      token: 'data.token',
      vat: 'data.vat',
    };

    return new Promise(resolve => resolve({ upsell: data }));
  }

  getTenancySubscription(get: TenancySubscriptionGet): Promise<TenancySubscriptionGetResponse> {
    const data = {
      status: UpsellStatusEnum.CREDTIT_CHECK,
      token: 'deposit subscription credit check token',
    };

    return new Promise(resolve => resolve({ upsellTenancySubscription: data }));
  }

  createTenancySubscription(create: TenancySubscriptionCreate): Promise<TenancySubscriptionCreateResponse> {
    const data = 'tenancy subscription id';

    LogOperations.debug('API|upsell|MOCK createTenancySubscription::POST data', create);
    LogOperations.debug('API|upsell|MOCK createTenancySubscription::POST body', data);

    return new Promise(resolve => resolve({ upsellId: data }));
  }
}
